import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import type { Agent } from '@/lib/supabase'

interface AuthContextType {
  agent: Agent | null
  userId: string | null          // raw Supabase auth user id
  isLoading: boolean
  isAuthenticated: boolean       // true = valid Supabase session (not gated on agent row)
  isAgentLoaded: boolean         // true = agents table lookup finished
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  agent: null,
  userId: null,
  isLoading: true,
  isAuthenticated: false,
  isAgentLoaded: false,
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [agent, setAgent]               = useState<Agent | null>(null)
  const [userId, setUserId]             = useState<string | null>(null)
  const [isLoading, setIsLoading]       = useState(true)
  const [isAgentLoaded, setIsAgentLoaded] = useState(false)

  async function loadAgent(uid: string) {
    setIsAgentLoaded(false)
    try {
      const { data } = await supabase
        .from('agents')
        .select('*')
        .eq('id', uid)
        .eq('is_active', true)
        .maybeSingle()          // maybeSingle: returns null (no error) if no row

      setAgent(data as Agent | null)
    } catch (err) {
      console.warn('[AuthContext] loadAgent error:', err)
      setAgent(null)
    }
    setIsAgentLoaded(true)
  }

  useEffect(() => {
    // Hard timeout so we never stay on the loading screen forever
    const timeout = setTimeout(() => {
      setIsLoading(false)
    }, 5000)

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      clearTimeout(timeout)
      if (session?.user) {
        setUserId(session.user.id)
        void loadAgent(session.user.id)   // non-blocking — don't await
      }
      setIsLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT' || !session) {
          setUserId(null)
          setAgent(null)
          setIsAgentLoaded(false)
          setIsLoading(false)
          return
        }
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session.user) {
          setUserId(session.user.id)
          void loadAgent(session.user.id)   // non-blocking
          setIsLoading(false)
        }
      }
    )

    return () => {
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function signOut() {
    await supabase.auth.signOut()
    setUserId(null)
    setAgent(null)
    setIsAgentLoaded(false)
  }

  return (
    <AuthContext.Provider value={{
      agent,
      userId,
      isLoading,
      // ✅ Authenticated = valid Supabase session  (not gated on agent row existing)
      // This means login always works for any Supabase user.
      // Role-based UI (agent features) checks `agent !== null` separately.
      isAuthenticated: !!userId,
      isAgentLoaded,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

// Role helpers → import from '@/context/useRoles'
